import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    ArrowLeft, House as HouseIcon, Lock, ExternalLink,
    ChevronRight, Loader2, AlertTriangle, Cpu, MapPinned,
    Building2, Calendar
} from 'lucide-react';


const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5022';

// ============================================
// ✅ Маппинг типов устройств (как в HousePage)
// ============================================
const DEVICE_TYPE_MAP = {
    'Aksioma01': 'Аксиома-01',
    'Aksioma03': 'Аксиома-03',
    'Karat307': 'Карат-307'
};

const StreetPage = ({
    city,
    street,
    onBack,
    isDesktop = false,
    onSelect,          // ✅ Единый обработчик навигации (как в DeviceTree)
    onSelectHouse,     // ✅ Для обратной совместимости
    onSelectDevice     // ✅ Для обратной совместимости
}) => {
    const [streetData, setStreetData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedHouses, setExpandedHouses] = useState({});

    useEffect(() => {
        loadStreetData();
    }, [city, street]);

    // ============================================
    // ✅ Загрузка данных улицы с сервера
    // ============================================
    const loadStreetData = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');

            const response = await axios.get(
                `${API_URL}/api/street/${encodeURIComponent(city)}/${encodeURIComponent(street)}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            setStreetData(response.data || []);
        } catch (err) {
            console.error('❌ Ошибка загрузки данных улицы:', err);
            if (err.response?.status === 401) {
                setError('Необходима авторизация');
            } else if (err.response?.status === 404) {
                setStreetData([]);
            } else {
                setError('Не удалось загрузить данные улицы');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // ============================================
    // ✅ Раскрытие/сворачивание дома
    // ============================================
    const toggleHouse = (houseNumber) => {
        setExpandedHouses(prev => ({ ...prev, [houseNumber]: !prev[houseNumber] }));
    };

    // ============================================
    // ✅ Статус опломбирования
    // ============================================
    const getPlombStatus = (node) => {
        if (!node.isOplombirovan) {
            return { text: 'Нет', color: 'text-slate-400', bg: 'bg-slate-600/20', border: 'border-slate-600/30' };
        }
        if (!node.oplombirovanValidUntil) {
            return { text: 'Да', color: 'text-green-400', bg: 'bg-green-600/20', border: 'border-green-600/30' };
        }

        const parts = node.oplombirovanValidUntil.split('.');
        if (parts.length !== 3) {
            return { text: 'Да', color: 'text-green-400', bg: 'bg-green-600/20', border: 'border-green-600/30' };
        }

        const [day, month, year] = parts;
        const validUntilDate = new Date(`${year}-${month}-${day}`);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        validUntilDate.setHours(0, 0, 0, 0);

        if (validUntilDate < today) {
            return { text: 'Просрочено', color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/30' };
        }

        const diffDays = Math.ceil((validUntilDate - today) / (1000 * 60 * 60 * 24));
        if (diffDays <= 30) {
            return { text: 'Скоро истечёт', color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500/30' };
        }

        return { text: 'Да', color: 'text-green-400', bg: 'bg-green-600/20', border: 'border-green-600/30' };
    };

    // ============================================
    // ✅ Переход к тепловычислителю
    // ============================================
    const handleDeviceClick = (heatComputer, houseNumber) => {
        if (!heatComputer) return;

        const deviceTypeRu = DEVICE_TYPE_MAP[heatComputer.deviceType] || heatComputer.deviceType;
        const device = {
            ...heatComputer,
            name: heatComputer.deviceName,
            deviceType: deviceTypeRu,
            fullName: `${deviceTypeRu} ${heatComputer.deviceName || ''}`.trim(),
            city,
            street,
            houseNumber,
            status: 0
        };

        // ✅ Поддержка обоих вариантов пропсов
        if (onSelect) {
            onSelect({ type: 'device', data: device });
        } else if (onSelectDevice) {
            onSelectDevice({ type: 'device', data: device });
        } else {
            console.warn('⚠️ Не передан обработчик onSelect или onSelectDevice для перехода к устройству');
        }
    };

    // ============================================
    // ✅ Переход к дому
    // ============================================
    const handleHouseClick = (houseNumber) => {
        const payload = {
            type: 'house',
            data: { city, street, house: `Дом ${houseNumber}` }
        };

        // ✅ Поддержка обоих вариантов пропсов
        if (onSelect) {
            onSelect(payload);
        } else if (onSelectHouse) {
            onSelectHouse(payload);
        } else {
            console.warn('⚠️ Не передан обработчик onSelect или onSelectHouse для перехода к дому');
        }
    };

    // ============================================
    // ✅ Состояния загрузки и ошибки
    // ============================================
    if (isLoading) {
        return (
            <div className="h-full flex items-center justify-center">
                <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="h-full flex items-center justify-center p-4">
                <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    <span>{error}</span>
                </div>
            </div>
        );
    }

    // ============================================
    // ✅ Основной рендер
    // ============================================
    return (
        <div className="h-full flex flex-col">
            {/* Шапка */}
            <div className="bg-slate-800 border-b border-slate-700 p-4">
                {!isDesktop && (
                    <div className="flex items-center gap-3 mb-3">
                        <button
                            onClick={onBack}
                            className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition text-sm"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Назад
                        </button>
                    </div>
                )}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="flex items-center gap-2 text-lg sm:text-xl font-bold text-white">
                            <MapPinned className="w-6 h-6 shrink-0 text-cyan-400" />
                            <span>{street}</span>
                        </h2>
                        <p className="text-slate-400 text-sm">{city}</p>
                    </div>
                    <div className="text-slate-400 text-sm">
                        Домов: <span className="text-white font-semibold">{streetData.length}</span>
                    </div>
                </div>
            </div>

            {/* Содержимое */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-6">
                {streetData.length === 0 ? (
                    <div className="bg-slate-800 rounded-xl p-12 border border-slate-700 text-center">
                        <Building2 className="w-16 h-16 text-slate-600 mx-auto mb-3" />
                        <p className="text-slate-400">На этой улице нет домов</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {streetData.map(house => {
                            const houseNumber = house.houseNumber || house.number;
                            const isExpanded = expandedHouses[houseNumber];
                            const nodes = house.meteringNodes || [];
                            const totalDevices = nodes.reduce((acc, n) => acc + (n.heatComputer ? 1 : 0), 0);

                            return (
                                <div
                                    key={houseNumber}
                                    className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden"
                                >
                                    {/* Строка дома */}
                                    <div
                                        className="px-4 py-3 bg-slate-700/50 border-b border-slate-700 flex items-center justify-between gap-3 cursor-pointer hover:bg-slate-700/70 transition"
                                        onClick={() => toggleHouse(houseNumber)}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <ChevronRight
                                                className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                                            />
                                            <HouseIcon className="w-5 h-5 text-amber-400 flex-shrink-0" />
                                            <div className="min-w-0">
                                                <div className="text-white font-semibold">Дом {houseNumber}</div>
                                                <div className="text-slate-400 text-xs truncate">
                                                    {nodes.length > 0
                                                        ? `Узлов учёта: ${nodes.length} • Тепловычислителей: ${totalDevices}`
                                                        : 'Нет узлов учёта'}
                                                </div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation(); // ✅ Предотвращаем сворачивание при клике на кнопку
                                                handleHouseClick(houseNumber);
                                            }}
                                            className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-medium transition flex-shrink-0"
                                            title="Открыть страницу дома"
                                        >
                                            <ExternalLink className="w-3.5 h-3.5" />
                                            <span className="hidden sm:inline">Открыть дом</span>
                                        </button>
                                    </div>

                                    {/* Раскрытое содержимое дома */}
                                    {isExpanded && (
                                        <div className="p-3">
                                            {nodes.length === 0 ? (
                                                <div className="text-slate-500 text-sm text-center py-6">
                                                    Узлы учёта не добавлены
                                                </div>
                                            ) : (
                                                <div className="overflow-x-auto">
                                                    <table className="w-full text-sm">
                                                        <thead>
                                                            <tr className="text-slate-400 text-xs border-b border-slate-700">
                                                                <th className="text-left py-2 px-2 font-medium">Узел учёта</th>
                                                                <th className="text-left py-2 px-2 font-medium">Опломбирован</th>
                                                                <th className="text-left py-2 px-2 font-medium">
                                                                    <span className="flex items-center gap-1">
                                                                        <Calendar className="w-3 h-3" />
                                                                        Дата опломбировки
                                                                    </span>
                                                                </th>
                                                                <th className="text-left py-2 px-2 font-medium">
                                                                    <span className="flex items-center gap-1">
                                                                        <Calendar className="w-3 h-3" />
                                                                        Действительно до
                                                                    </span>
                                                                </th>
                                                                <th className="text-left py-2 px-2 font-medium">Тепловычислитель</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {nodes.map(node => {
                                                                const plombStatus = getPlombStatus(node);
                                                                const hc = node.heatComputer;
                                                                const hcDisplayName = hc
                                                                    ? `${DEVICE_TYPE_MAP[hc.deviceType] || hc.deviceType} ${hc.deviceName || ''}`.trim()
                                                                    : '';

                                                                return (
                                                                    <tr
                                                                        key={node.id}
                                                                        className="border-b border-slate-700/50 hover:bg-slate-700/20 transition"
                                                                    >
                                                                        <td className="py-2.5 px-2">
                                                                            <div className="text-white font-medium">
                                                                                {node.name}
                                                                            </div>
                                                                            {node.description && (
                                                                                <div className="text-slate-500 text-xs truncate max-w-[200px]">
                                                                                    {node.description}
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                        <td className="py-2.5 px-2">
                                                                            <span
                                                                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${plombStatus.bg} ${plombStatus.color} ${plombStatus.border}`}
                                                                            >
                                                                                <Lock className="w-3 h-3" />
                                                                                {plombStatus.text}
                                                                            </span>
                                                                        </td>
                                                                        <td className="py-2.5 px-2 text-slate-300 whitespace-nowrap">
                                                                            {node.oplombirovanDate || '—'}
                                                                        </td>
                                                                        <td className="py-2.5 px-2 text-slate-300 whitespace-nowrap">
                                                                            {node.oplombirovanValidUntil || '—'}
                                                                        </td>
                                                                        <td className="py-2.5 px-2">
                                                                            {hc ? (
                                                                                <button
                                                                                    onClick={() => handleDeviceClick(hc, houseNumber)}
                                                                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-600/20 hover:bg-indigo-600/40 text-indigo-300 rounded text-xs transition group border border-indigo-500/30"
                                                                                    title="Открыть тепловычислитель"
                                                                                >
                                                                                    <Cpu className="w-3.5 h-3.5 flex-shrink-0" />
                                                                                    <span className="truncate max-w-[180px]">
                                                                                        {hcDisplayName}
                                                                                    </span>
                                                                                    <ExternalLink className="w-3 h-3 opacity-60 group-hover:opacity-100 flex-shrink-0" />
                                                                                </button>
                                                                            ) : (
                                                                                <span className="text-slate-500 text-xs italic">
                                                                                    Не добавлен
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default StreetPage;